import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

export interface TooltipConfig {
  id: string;
  target: string; // CSS selector for the target element
  title: string;
  content: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  trigger: 'hover' | 'click' | 'focus' | 'manual';
  delay?: number;
  offset?: number;
  arrow?: boolean;
  maxWidth?: number;
  className?: string;
}

interface TooltipProps extends TooltipConfig {
  isVisible: boolean;
  onClose?: () => void;
  targetElement?: HTMLElement;
}

const Tooltip: React.FC<TooltipProps> = ({
  title,
  content,
  position = 'auto',
  isVisible,
  onClose,
  targetElement,
  offset = 8,
  arrow: _arrow = true,
  maxWidth = 300,
  className = '',
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [calculatedPosition, setCalculatedPosition] = useState(position);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});

  const calculatePosition = useCallback(() => {
    if (!targetElement || !tooltipRef.current) return;

    const targetRect = targetElement.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let finalPosition = position;
    let top = 0;
    let left = 0;

    // Auto-calculate position if needed
    if (position === 'auto') {
      const spaceTop = targetRect.top;
      const spaceBottom = viewportHeight - targetRect.bottom;
      const spaceLeft = targetRect.left;
      const spaceRight = viewportWidth - targetRect.right;

      if (spaceBottom >= tooltipRect.height + offset) {
        finalPosition = 'bottom';
      } else if (spaceTop >= tooltipRect.height + offset) {
        finalPosition = 'top';
      } else if (spaceRight >= tooltipRect.width + offset) {
        finalPosition = 'right';
      } else if (spaceLeft >= tooltipRect.width + offset) {
        finalPosition = 'left';
      } else {
        finalPosition = 'bottom'; // Default fallback
      }
    }

    // Calculate position based on final position
    switch (finalPosition) {
      case 'top':
        top = targetRect.top - tooltipRect.height - offset;
        left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = targetRect.bottom + offset;
        left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
        left = targetRect.left - tooltipRect.width - offset;
        break;
      case 'right':
        top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
        left = targetRect.right + offset;
        break;
    }

    // Ensure tooltip stays within viewport
    left = Math.max(8, Math.min(left, viewportWidth - tooltipRect.width - 8));
    top = Math.max(8, Math.min(top, viewportHeight - tooltipRect.height - 8));

    setCalculatedPosition(finalPosition);
    setTooltipStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      maxWidth: `${maxWidth}px`,
      zIndex: 1000,
    });
  }, [targetElement, position, offset, maxWidth]);

  useEffect(() => {
    if (isVisible && targetElement) {
      calculatePosition();

      const handleResize = () => calculatePosition();
      const handleScroll = () => calculatePosition();

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isVisible, targetElement, calculatePosition]);

  if (!isVisible) return null;

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className={`tooltip show ${calculatedPosition} ${className}`}
      style={tooltipStyle}
      role="tooltip"
      aria-live="polite"
    >
      {title && (
        <div className="font-semibold text-white mb-1 text-sm">
          {title}
        </div>
      )}
      <div className="text-sm text-gray-100 leading-relaxed">
        {content}
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-1 right-1 p-1 text-gray-300 hover:text-white transition-colors"
          aria-label="Close tooltip"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );

  return createPortal(tooltipContent, document.body);
};

interface TooltipSystemProps {
  tooltips: TooltipConfig[];
}

export const TooltipSystem: React.FC<TooltipSystemProps> = ({
  tooltips
}) => {
  const [activeTooltips, setActiveTooltips] = useState<Set<string>>(new Set());
  const [hoveredElements, setHoveredElements] = useState<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    const handleMouseEnter = (event: Event, tooltip: TooltipConfig) => {
      if (tooltip.trigger === 'hover') {
        const target = event.target as HTMLElement;
        setHoveredElements(prev => new Map(prev).set(tooltip.id, target));

        if (tooltip.delay) {
          setTimeout(() => {
            setActiveTooltips(prev => new Set(prev).add(tooltip.id));
          }, tooltip.delay);
        } else {
          setActiveTooltips(prev => new Set(prev).add(tooltip.id));
        }
      }
    };

    const handleMouseLeave = (tooltip: TooltipConfig) => {
      if (tooltip.trigger === 'hover') {
        setActiveTooltips(prev => {
          const newSet = new Set(prev);
          newSet.delete(tooltip.id);
          return newSet;
        });
        setHoveredElements(prev => {
          const newMap = new Map(prev);
          newMap.delete(tooltip.id);
          return newMap;
        });
      }
    };

    // Attach event listeners
    tooltips.forEach(tooltip => {
      const elements = document.querySelectorAll(tooltip.target);
      elements.forEach(element => {
        if (tooltip.trigger === 'hover') {
          element.addEventListener('mouseenter', (e) => handleMouseEnter(e, tooltip));
          element.addEventListener('mouseleave', () => handleMouseLeave(tooltip));
        }
      });
    });

    return () => {
      // Cleanup event listeners
      tooltips.forEach(tooltip => {
        const elements = document.querySelectorAll(tooltip.target);
        elements.forEach(element => {
          element.removeEventListener('mouseenter', (e) => handleMouseEnter(e, tooltip));
          element.removeEventListener('mouseleave', () => handleMouseLeave(tooltip));
        });
      });
    };
  }, [tooltips]);

  return (
    <>
      {/* Regular tooltips */}
      {tooltips.map(tooltip => (
        <Tooltip
          key={tooltip.id}
          {...tooltip}
          isVisible={activeTooltips.has(tooltip.id)}
          targetElement={hoveredElements.get(tooltip.id)}
          onClose={() => {
            setActiveTooltips(prev => {
              const newSet = new Set(prev);
              newSet.delete(tooltip.id);
              return newSet;
            });
          }}
        />
      ))}


    </>
  );
};

export default TooltipSystem;